import React, { useState, useEffect, useRef } from 'react';

import { uploadImageToCloudinaryWithProgress } from '../../../../api/external/cloudinary/upload';

import EditModalShell from '../forms/EditModalShell/EditModalShell';
import LoadingModal from '../LoadingModal/LoadingModal';
import AdminProgressBar from '../../AdminProgressBar/AdminProgressBar';
import ImageInputWithPreview from '../forms/ImageInputWithPreview/ImageInputWithPreview';
import FormField from '../forms/FormField/FormField';

import './BlogPostAuthorModal.css';

const BlogPostAuthorModal = ({
  onClose,
  onSubmit,
  blogPostAuthor,
  mode,
  errorMsg,
}) => {
  const initialFormState = {
    name: '',
    bio: '',
    avatarUrl: '',
    avatarPublicId: '',
  };

  const [formData, setFormData] = useState(initialFormState);

  const [selectedFiles, setSelectedFiles] = useState({}); // raw files
  const [imagePreviewUrls, setImagePreviewUrls] = useState({});

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  const { name, bio, avatarUrl } = formData;

  const firstInputRef = useRef();

  /* ---------- Init ---------- */

  useEffect(() => {
    setFormData({
      name: blogPostAuthor?.name || '',
      bio: blogPostAuthor?.bio || '',
      avatarUrl: blogPostAuthor?.avatarUrl || '',
      avatarPublicId: blogPostAuthor?.avatarPublicId || '',
    });

    setImagePreviewUrls({ avatarUrl: blogPostAuthor?.avatarUrl || '' });

    setSelectedFiles({});

    firstInputRef.current?.focus();
  }, [mode, blogPostAuthor]);

  /* ---------- Handlers ---------- */

  const handleInputChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files[0];
    if (!file) return;

    setSelectedFiles((prev) => ({ ...prev, [name]: file }));

    setImagePreviewUrls((prev) => ({
      ...prev,
      [name]: URL.createObjectURL(file),
    }));
  };

  const handleRemoveImage = (name) => {
    setSelectedFiles((prev) => ({ ...prev, [name]: null }));

    setImagePreviewUrls((prev) => ({
      ...prev,
      [name]: null,
    }));

    setFormData((prev) => ({
      ...prev,
      [name]: '',
      [`${name.replace(/Url$/, '')}PublicId`]: '',
    }));
  };

  /* ---------- Submit ---------- */

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setProgress(0);

    try {
      let finalData = { ...formData };

      if (selectedFiles?.avatarUrl) {
        const uploaded = await uploadImageToCloudinaryWithProgress(
          selectedFiles.avatarUrl,
          setProgress,
          'blog/authors',
          [0, 100],
        );

        finalData.avatarUrl = uploaded.url;
        finalData.avatarPublicId = uploaded.publicId;
      }

      setProgress(100);

      await onSubmit(finalData);
    } catch (err) {
      console.error(err);
      alert('Upload failed.');
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  /* ---------- Render ---------- */

  return (
    <EditModalShell
      formId='blog-post-modal-form'
      onClose={onClose}
      onSubmit={handleSubmit}
      mode={mode}
      isLoading={loading}
      itemLabel='Author'
    >
      {loading && (
        <LoadingModal>
          <AdminProgressBar progress={progress} />
        </LoadingModal>
      )}

      <div className='blog-post-author-modal-content-container'>
        <div className='blog-post-author-modal-pages-container'>
          <div className='blog-post-author-modal-blog-page-container'>
            <ImageInputWithPreview
              name='avatarUrl'
              imagePreviewUrl={imagePreviewUrls?.avatarUrl}
              imageUrl={avatarUrl}
              selectedImgAlt='Selected Author Image Preview'
              currentImgAlt='Current Author'
              onFileChange={handleFileChange}
              onRemoveImage={handleRemoveImage}
            />

            <div className='blog-post-author-model-error-container'>
              {errorMsg && <span>{errorMsg} </span>}
            </div>

            <FormField
              type='text'
              name='name'
              label='Name'
              value={name}
              onChange={handleInputChange}
              ref={firstInputRef}
              required
            />

            <FormField
              type='textarea'
              name='bio'
              label='Bio'
              value={bio}
              onChange={handleInputChange}
            />
          </div>
        </div>
      </div>
    </EditModalShell>
  );
};

export default BlogPostAuthorModal;
